import { useCallback, useEffect, useRef, useState } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (dir: number) => {
      setIndex((prev) => (prev + dir + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    const timer = window.setInterval(() => go(1), 5500);
    return () => window.clearInterval(timer);
  }, [go, index]);

  if (!items.length) return null;

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}剧照`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-10 flex items-center">
        <h1 className="hidden md:block absolute top-6 left-10 text-xs font-medium tracking-widest text-primary-200/90 uppercase">
          麻豆网站 · 最新影视资源大全与影视资讯平台
        </h1>
        <div key={index} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-foreground-950 mb-4">
            <i className="ri-fire-fill" />
            焦点推荐
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-background-50 leading-tight mb-3">
            {items[index].title}
          </h2>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-primary-100 mb-4">
            <span className="text-accent-400 font-bold text-base">
              <i className="ri-star-fill mr-1" />
              {items[index].rating.toFixed(1)}
            </span>
            <span>{items[index].year}</span>
            <span className="w-1 h-1 rounded-full bg-primary-300" />
            <span>{items[index].region}</span>
            <span className="w-1 h-1 rounded-full bg-primary-300" />
            <span>{items[index].genres.join(" / ")}</span>
          </div>
          <p className="text-sm md:text-base text-primary-100/90 leading-relaxed mb-6 max-w-xl line-clamp-2 md:line-clamp-3">
            {items[index].synopsis}
          </p>
          <div className="w-full flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index].id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={`/category/${items[index].type}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 border border-background-50/30 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              更多同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}